import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';

type Parsed = { title: string; projectId?: string; focus?: boolean; status?: any; dueAt?: number | null };

function parseOne(raw: string, projects: { id: string; name: string }[]): Parsed | null {
  let title = raw.trim();
  if (!title) return null;
  let focus = false;
  let status: any = 'inbox';
  let projectId: string | undefined = undefined;
  let dueAt: number | null = null;
  title = title.replace(/(^|\s)!today\b/gi, () => {
    focus = true;
    status = 'next';
    return ' ';
  });
  title = title.replace(/(^|\s)!next\b/gi, () => {
    status = 'next';
    return ' ';
  });
  title = title.replace(/(^|\s)!someday\b/gi, () => {
    status = 'someday';
    return ' ';
  });
  title = title.replace(/(^|\s)!waiting\b/gi, () => {
    status = 'waiting';
    return ' ';
  });
  title = title.replace(/\+([\w-]+)/g, (_m, name: string) => {
    const found = projects.find((p) => p.name.toLowerCase().startsWith(name.toLowerCase()));
    if (found) projectId = found.id;
    return '';
  });
  title = title.replace(/@(today|tomorrow|tom|tmrw)/gi, (_m, w: string) => {
    const d = new Date();
    if (/tomorrow|tom|tmrw/i.test(w)) d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    dueAt = d.getTime();
    return '';
  });
  title = title.replace(/\s+/g, ' ').trim();
  if (!title) return null;
  return { title, projectId, focus, status, dueAt };
}

export function QuickAdd({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });
  const qc = useQueryClient();

  const parsed = useMemo(() => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    return lines
      .map((l) => parseOne(l, (projects.data ?? []) as any))
      .filter((x): x is Parsed => !!x);
  }, [text, projects.data]);

  const mut = useMutation({
    mutationFn: async (items: Parsed[]) => {
      // Serial to keep ordering stable in lists.
      const created: any[] = [];
      for (const it of items) {
        const r = await api.createTask({
          title: it.title,
          projectId: it.projectId,
          focus: it.focus,
          status: it.status ?? 'inbox',
          dueAt: it.dueAt ?? null,
        } as any);
        created.push(r);
      }
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      pushToast(
        created.length === 1
          ? "Added. Don't just stare at it."
          : `Added ${created.length}. The pile thickens.`,
        'success',
      );
    },
    onError: (e: any) => pushToast(e?.message ?? 'Could not add.', 'error'),
  });

  useEffect(() => {
    ref.current?.focus();
  }, []);

  function submit(closeAfter: boolean) {
    if (parsed.length === 0) return;
    mut.mutate(parsed);
    setText('');
    if (closeAfter) onClose();
  }

  const buttonLabel =
    parsed.length === 0 ? 'Add' : parsed.length === 1 ? 'Add 1' : `Add ${parsed.length} tasks`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-end sm:place-items-center" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl border border-zinc-800 bg-zinc-900 p-3 animate-pop max-h-[90dvh] overflow-y-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs text-zinc-500 mb-1.5 flex items-center justify-between">
          <span>Quick add{parsed.length > 1 ? ` · ${parsed.length} on the slab` : ''}</span>
          <span className="hidden sm:block">Enter to save · Shift+Enter for another · Esc to close</span>
        </div>
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              // Cmd/Ctrl+Enter always submits and closes.
              e.preventDefault();
              submit(true);
            } else if (e.key === 'Enter' && !e.shiftKey) {
              // Plain Enter submits and closes (single-line capture).
              e.preventDefault();
              submit(true);
            }
            // Shift+Enter inserts a newline — textarea handles natively, builds up bulk.
          }}
          rows={parsed.length > 1 ? Math.min(parsed.length + 1, 8) : 2}
          placeholder='Capture a thought. Shift+Enter for more on the same trip. Try "Pay water bill !today +Personal"'
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 outline-none focus:border-amber-500 text-sm resize-none"
        />
        <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">!today</span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">!next</span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">!waiting</span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">+Project</span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">@today</span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5">@tomorrow</span>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-zinc-400">Cancel</button>
            <button
              onClick={() => submit(true)}
              className="px-3 py-1.5 rounded bg-amber-500 text-black font-medium disabled:opacity-50"
              disabled={parsed.length === 0 || mut.isPending}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
